'use client';

import { SectionCard, SectionParagraph } from './section-card';
import { SectionGrid } from './section-grid';
import { HorizontalStageFlow } from './stage-flow';
import type { DefinitionServiceMap } from '@/hooks/use-definition';

interface ServiceMapTemplateProps {
  serviceMap: DefinitionServiceMap;
}

export function ServiceMapTemplate({ serviceMap }: ServiceMapTemplateProps) {
  const stages = Array.isArray(serviceMap.serviceStages) ? serviceMap.serviceStages : [];

  return (
    <div className="space-y-6">
      {/* Overview */}
      {serviceMap.description && (
        <SectionCard title="Overview">
          <SectionParagraph text={serviceMap.description} />
        </SectionCard>
      )}

      {/* Service Stages */}
      {stages.length > 0 && (
        <SectionCard title="Service Stages">
          <HorizontalStageFlow stages={stages} />
        </SectionCard>
      )}

      {/* Frontstage & Backstage */}
      {(serviceMap.frontstage || serviceMap.backstage) && (
        <SectionGrid>
          {serviceMap.frontstage && (
            <SectionCard title="Frontstage (Customer-facing)" dotColor="bg-blue-500" titleColor="text-blue-700" borderColor="border-blue-200">
              <FrontstageContent data={serviceMap.frontstage} />
            </SectionCard>
          )}
          {serviceMap.backstage && (
            <SectionCard title="Backstage (Internal)" dotColor="bg-purple-500" titleColor="text-purple-700" borderColor="border-purple-200">
              <BackstageContent data={serviceMap.backstage} />
            </SectionCard>
          )}
        </SectionGrid>
      )}

      {/* Systems & Failure Points */}
      {(serviceMap.systems || serviceMap.failurePoints) && (
        <SectionGrid>
          {serviceMap.systems && (
            <SectionCard title="Supporting Systems" dotColor="bg-gray-500">
              <SectionParagraph text={serviceMap.systems} />
            </SectionCard>
          )}
          {serviceMap.failurePoints && (
            <SectionCard title="Failure Points" dotColor="bg-red-500" titleColor="text-red-600" borderColor="border-red-200">
              <SectionParagraph text={serviceMap.failurePoints} />
            </SectionCard>
          )}
        </SectionGrid>
      )}
    </div>
  );
}

function FrontstageContent({ data }: { data: unknown }) {
  if (typeof data === 'string') return <SectionParagraph text={data} />;
  if (!Array.isArray(data)) return null;

  return (
    <div className="space-y-4">
      {data.map((item: { actor?: string; actions?: string[]; touchpoints?: string[] }, i: number) => (
        <div key={i}>
          {item.actor && <h4 className="font-medium text-sm text-gray-900 mb-1">{item.actor}</h4>}
          {Array.isArray(item.actions) && (
            <ul className="space-y-1 mb-2">
              {item.actions.map((a, ai) => (
                <li key={ai} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          )}
          {Array.isArray(item.touchpoints) && item.touchpoints.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {item.touchpoints.map((tp, ti) => (
                <span key={ti} className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs">
                  {tp}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BackstageContent({ data }: { data: unknown }) {
  if (typeof data === 'string') return <SectionParagraph text={data} />;
  if (!Array.isArray(data)) return null;

  return (
    <div className="space-y-4">
      {data.map((item: { system?: string; processes?: string[] }, i: number) => (
        <div key={i}>
          {item.system && <h4 className="font-medium text-sm text-gray-900 mb-1">{item.system}</h4>}
          {Array.isArray(item.processes) && (
            <ul className="space-y-1">
              {item.processes.map((p, pi) => (
                <li key={pi} className="text-sm text-gray-600 flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
